import { HStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { Fontisto } from '@expo/vector-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Animated, Easing, Pressable } from 'react-native'

const COUNTDOWN_IN_SECONDS = 5
const INTERVAL_IN_MS = 1000

const BookingActionType = {
	PREPARING: 'PREPARING',
	BOOKING: 'BOOKING',
}

export type BookingActionProps = {
	onCancelled: () => void
	onTriggered: () => void
}

export const BookingAction = ({ onCancelled, onTriggered }: BookingActionProps) => {
	const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_IN_SECONDS)
	const [actionType, setActionType] = useState(BookingActionType.PREPARING)
	const interval = useRef<ReturnType<typeof setInterval> | null>(null)

	const translation = useRef(new Animated.Value(60)).current
	const bookingAnimation = Animated.timing(translation, {
		toValue: 0,
		duration: 200,
		easing: Easing.linear,
		useNativeDriver: true,
	})

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		bookingAnimation.start()
	}, [])

	const cancelAppointmentBooking = () => {
		if (actionType === BookingActionType.PREPARING) {
			onCancelled()
		}
	}

	const labelText = useMemo(() => {
		return actionType === BookingActionType.PREPARING
			? `Booking in ${secondsLeft}...`
			: 'Booking...'
	}, [actionType, secondsLeft])

	useEffect(() => {
		interval.current = setInterval(() => {
			setSecondsLeft((prevSecondsLeft) => prevSecondsLeft - 1)
			if (secondsLeft - 1 <= 0) {
				clearInterval(interval.current as NodeJS.Timeout)
				onTriggered()
				setActionType(BookingActionType.BOOKING)
			}
		}, INTERVAL_IN_MS)

		return () => clearInterval(interval.current as NodeJS.Timeout)
	}, [secondsLeft, onTriggered])

	return (
		<Animated.View
			style={{
				position: 'absolute',
				bottom: 60,
				width: '100%',
				display: 'flex',
				alignItems: 'center',
				transform: [{ translateY: translation }],
			}}
		>
			<HStack className="w-44 items-center justify-between bg-palette_3 pl-6 pr-2 py-2 rounded-full border-[1px] border-palette_2">
				<Text className="text-palette_2">{labelText}</Text>
				<Pressable role="button" onPress={cancelAppointmentBooking}>
					<Fontisto name="close" size={24} color="#F8EDDF" />
				</Pressable>
			</HStack>
		</Animated.View>
	)
}
